import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building, Check, CheckCircle, Link, Save, Search, User, Users, X } from 'lucide-react';

const MailLinkModal = ({
  addToSelectedLinks,
  linkSearchQuery,
  linkSearchResults,
  linkSuggestions,
  removeFromSelectedLinks,
  saveLinkAndProcess,
  searchEntitiesForLink,
  selectedLinks,
  setLinkSearchQuery,
  setShowLinkModal
}) => {
  const { t } = useTranslation('admin');
  return (
    <>
        <div data-testid="mail-link-modal" className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-blue-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Link className="text-white" size={20} />
                  <h2 className="text-lg font-bold text-white">{t('mail.linkToCrm')}</h2>
                </div>
                <button onClick={() => setShowLinkModal(false)} className="text-white/80 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[50vh] overflow-y-auto">
              {/* Suggestions automatiques */}
              {linkSuggestions.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">{t('mail.autoSuggestions')}</p>
                  <div className="space-y-2">
                    {linkSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => addToSelectedLinks(suggestion)}
                        disabled={selectedLinks.find(l => l.type === suggestion.type && l.id === suggestion.id)}
                        className={`w-full p-3 rounded-lg border text-left flex items-center justify-between ${
                          selectedLinks.find(l => l.type === suggestion.type && l.id === suggestion.id)
                            ? 'bg-green-50 border-green-200'
                            : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {suggestion.type === 'client' && <User size={16} className="text-blue-600" />}
                          {suggestion.type === 'courtier' && <Users size={16} className="text-red-600" />}
                          {suggestion.type === 'compagnie' && <Building size={16} className="text-slate-600" />}
                          <div>
                            <p className="font-medium text-slate-900">{suggestion.name}</p>
                            <p className="text-xs text-slate-500">{suggestion.type} • {suggestion.email}</p>
                          </div>
                        </div>
                        {selectedLinks.find(l => l.type === suggestion.type && l.id === suggestion.id) && (
                          <CheckCircle size={16} className="text-green-600" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recherche manuelle */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">{t('mail.searchLabel')}</p>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t('mail.linkSearchPlaceholder')}
                    value={linkSearchQuery}
                    onChange={(e) => {
                      setLinkSearchQuery(e.target.value);
                      searchEntitiesForLink(e.target.value);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {/* Résultats de recherche */}
                {linkSearchResults.length > 0 && (
                  <div className="mt-2 border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                    {linkSearchResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => addToSelectedLinks(result)}
                        className="w-full p-2 text-left hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-0"
                      >
                        {result.type === 'client' && <User size={14} className="text-blue-600" />}
                        {result.type === 'courtier' && <Users size={14} className="text-red-600" />}
                        {result.type === 'compagnie' && <Building size={14} className="text-slate-600" />}
                        <span className="text-sm">{result.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Liaisons sélectionnées */}
              {selectedLinks.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">{t('mail.selectedLinksLabel')}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedLinks.map((link, idx) => (
                      <span
                        key={idx}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                          link.type === 'client' ? 'bg-blue-100 text-blue-700' :
                          link.type === 'courtier' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {link.name}
                        <button onClick={() => removeFromSelectedLinks(link)} className="hover:opacity-70">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-2">
              <button
                onClick={() => saveLinkAndProcess(true)}
                disabled={selectedLinks.length === 0}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                <Check size={16} />
                {t('mail.linkAndProcess')}
              </button>
              <button
                onClick={() => saveLinkAndProcess(false)}
                disabled={selectedLinks.length === 0}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
              >
                <Save size={16} />
                {t('mail.saveLinks')}
              </button>
              <button
                onClick={() => setShowLinkModal(false)}
                className="w-full px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium"
              >
                {t('mail.cancelBtn')}
              </button>
            </div>
          </div>
        </div>
    </>
  );
};

export default MailLinkModal;
